import VueDropzone from 'vue2-dropzone'
import communicationNoticeService from '@/services/provider/communicationNotices'
import recallService from '@/services/provider/recall'

const MAX_TOTAL_SIZE_BYTES = 25 * 1024 * 1024
const CANCEL_PROCESSING_CONFIRM_MESSAGE = 'Your document is still being processed. Do you want to cancel processing without saving the extracted details?'

export default {
    components: {
        VueDropzone,
    },
    mounted() {
        window.addEventListener('beforeunload', this.onBeforeUnload)
        window.addEventListener('keydown', this.onRefreshShortcut)
    },
    data() {
        return {
            files: [],
            modalError: '',
            showUploadLayout: true,
            isAiProcessing: false,
            isProcessingUpload: false,
            isRefreshConfirmOpen: false,
            processingJobId: null,
            processingProgress: 0,
            processingStartedAt: null,
            processingProgressTimer: null,
            processingPollTimer: null,
            processingPollEveryMs: 3000,
            processingMaxWindowMs: 90000,
            processingProgressPaused: false,
            showTimeoutAlert: false,
            timeoutAlertShown: false,
            dropzoneOptions: {
                url: '#',
                acceptedFiles: '.pdf,.jpg,.jpeg,.png',
                maxFiles: null,
                previewsContainer: false,
                autoProcessQueue: false,
            },
            uploadedFilesJobId: null,
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload)
        window.removeEventListener('keydown', this.onRefreshShortcut)
        this.cleanupRecallAiTimers()
    },
    methods: {
        onBeforeUnload(event) {
            if (!this.isAiProcessing) {
                return
            }

            event.preventDefault()
            event.returnValue = CANCEL_PROCESSING_CONFIRM_MESSAGE
            return CANCEL_PROCESSING_CONFIRM_MESSAGE
        },
        async onRefreshShortcut(event) {
            if (!this.isAiProcessing || this.isRefreshConfirmOpen) {
                return
            }

            const key = (event.key || '').toLowerCase()
            const isF5 = key === 'f5'
            const isReloadShortcut = (event.ctrlKey || event.metaKey) && key === 'r'

            if (!isF5 && !isReloadShortcut) {
                return
            }

            event.preventDefault()
            this.isRefreshConfirmOpen = true

            try {
                const confirmed = await this._showConfirm(
                    CANCEL_PROCESSING_CONFIRM_MESSAGE,
                    {
                        title: 'Cancel Processing',
                        variant: 'warning',
                        saveLabel: 'Yes',
                        cancelLabel: 'No'
                    }
                )
                if (confirmed) {
                    this.cleanupRecallAiTimers()
                    this.isAiProcessing = false
                    if (this.processingJobId) {
                        await recallService.cancelRecallAIJob(this.processingJobId)
                    }
                    this.processingJobId = null
                    window.location.reload()
                }
            } finally {
                this.isRefreshConfirmOpen = false
            }
        },
        forceRerenderInsert() {
            this.resetState()
            this.$refs.modal.show()
        },
        resetState() {
            this.cleanupRecallAiTimers()
            this.files = []
            this.modalError = ''
            this.showUploadLayout = true
            this.isAiProcessing = false
            this.isProcessingUpload = false
            this.processingJobId = null
            this.processingProgress = 0
            this.processingStartedAt = null
            this.processingProgressPaused = false
            this.showTimeoutAlert = false
            this.timeoutAlertShown = false
            if (this.$refs.dropzone) {
                this.$refs.dropzone.removeAllFiles()
            }
        },
        addSuccess(file) {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png']
            const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png']
            const ext = file.name.split('.').pop().toLowerCase()

            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
                this.modalError = '*Invalid file type. Please upload a PDF or image file.*'
                if (this.$refs.dropzone) {
                    this.$refs.dropzone.removeFile(file)
                }
                return
            }

            const newTotal = this.files.reduce((sum, f) => sum + f.size, 0) + file.size
            if (newTotal > MAX_TOTAL_SIZE_BYTES) {
                this.modalError = '*File size exceeds the 25MB limit*'
                if (this.$refs.dropzone) {
                    this.$refs.dropzone.removeFile(file)
                }
                return
            }

            this.modalError = ''
            this.files.push(file)
        },
        removeFile(index) {
            const file = this.files[index]
            if (this.$refs.dropzone) {
                this.$refs.dropzone.removeFile(file)
            }
            this.files.splice(index, 1)
            this.modalError = ''
        },
        async submit() {
            if (this.files.length === 0) {
                this.modalError = '*Please select at least one file before submitting.*'
                return
            }

            const formData = new FormData()
            this.files.forEach(file => {
                formData.append('files', file)
            })
            this.isProcessingUpload = true
            const resp = await communicationNoticeService.bulkUpload(formData)
            this.uploadedFilesJobId = resp.data.d.jobId
            this.isProcessingUpload = false
            if (resp.error) {
                this.modalError = resp.errorMsg || '*Failed to upload files. Please try again.*'
                return
            }
            this.isAiProcessing = true
            this.showUploadLayout = false
            const jobId = this.extractRecallAiJobId(resp?.data?.d || resp?.data || resp)
            if (!jobId) {
                this.handleRecallAiFailure()
                return
            }
            this.processingJobId = jobId
            this.processingProgress = 0
            this.startDeterministicRecallProgress()
            this.startRecallStatusPolling()
        },
        cleanupRecallAiTimers() {
            if (this.processingProgressTimer) {
                clearInterval(this.processingProgressTimer)
                this.processingProgressTimer = null
            }
            if (this.processingPollTimer) {
                clearInterval(this.processingPollTimer)
                this.processingPollTimer = null
            }
        },
        startDeterministicRecallProgress() {
            this.cleanupRecallAiTimers()
            this.processingStartedAt = Date.now()
            this.processingProgressTimer = setInterval(() => {
                if (this.processingProgressPaused) {
                    return
                }

                const elapsedMs = Date.now() - this.processingStartedAt
                let nextProgress = 95

                if (elapsedMs <= 30000) {
                    nextProgress = (elapsedMs / 30000) * 65
                } else if (elapsedMs <= this.processingMaxWindowMs) {
                    nextProgress = 65 + (((elapsedMs - 30000) / 60000) * 30)
                }

                this.processingProgress = Math.min(95, Number(nextProgress.toFixed(2)))
            }, 200)
        },
        startRecallStatusPolling() {
            this.processingPollTimer = setInterval(() => {
                this.pollRecallAiStatus()
            }, this.processingPollEveryMs)
        },
        async pollRecallAiStatus() {
            if (!this.processingJobId) {
                return
            }

            try {
                const elapsedMs = Date.now() - this.processingStartedAt
                const statusResp = await recallService.getRecallAIPipelineStatus(this.processingJobId, {
                    stopDefaultLoader: true
                })
                if (statusResp?.error) {
                    return
                }

                const statusPayload = statusResp?.data?.d || statusResp?.data || statusResp
                const statusValue = (this.extractRecallAiStatus(statusPayload) || '').toUpperCase()
                // // Update jobId from payload if available
                // const jobIdFromPayload = this.extractJobIdFromStatusPayload(statusPayload)
                // if (jobIdFromPayload) {
                //     this.JobId = jobIdFromPayload
                // }

                // Check if we've hit 90 seconds and status is still processing
                if (elapsedMs >= this.processingMaxWindowMs && !this.timeoutAlertShown && statusValue === 'PROCESSING') {
                    this.handleProcessingTimeout()
                    return
                }

                if (statusValue === 'SUCCEEDED' || statusValue === 'PROCESSED') {
                    this.handleRecallAiSuccess(statusPayload)
                } else if (statusValue === 'ERRORED' || statusValue === 'FAILED' || statusValue === 'TIMED_OUT' || statusValue === 'ABORTED') {
                    this.handleRecallAiFailure()
                }
            } catch (error) {
                // Keep polling until success or explicit failure status is returned.
            }
        },
        handleProcessingTimeout() {
            this.timeoutAlertShown = true
            this.processingProgressPaused = true
            this.showTimeoutAlert = true
            if (this.processingPollTimer) {
                clearInterval(this.processingPollTimer)
                this.processingPollTimer = null
            }
        },
        extractRecallAiJobId(payload) {
            if (!payload) { return null }
            return payload.ExecutionArn || payload.executionArn || null
        },
        // extractJobIdFromStatusPayload(payload) {
        //     if (!payload) { return null }
        //     return payload.jobId || payload.job_id || payload.ExecutionArn || payload.executionArn || null
        // },
        extractRecallAiStatus(payload) {
            if (!payload) { return null }
            return payload.status || null
        },
        extractRecallAiSourceId(payload) {
            if (!payload) { return null }
            return payload.recallFeedSourceId || payload.sourceId || payload.source_id || payload.recallReviewId || null
        },
        handleRecallAiSuccess(statusPayload) {
            this.cleanupRecallAiTimers()
            this.processingProgress = 100
            const sourceId = this.extractRecallAiSourceId(statusPayload)

            setTimeout(() => {
                this.isAiProcessing = false
                this.$refs.modal.hide()
                this.routeToRecallEntry(sourceId)
            }, 800)
        },
        handleRecallAiFailure() {
            this.cleanupRecallAiTimers()
            this.isAiProcessing = false
            this.$refs.modal.hide()
            this._showNotify(
                'System Error',
                'We ran into an unexpected issue processing this document. To avoid any delays, let\'s proceed to enter the details manually.',
                'danger',
                { hideFooter: false }
            )
        },
        routeToRecallEntry(sourceId = null) {
            localStorage.setItem('recallMode', 'ADD')
            // localStorage.removeItem('RecallFeedSourceId')
            localStorage.removeItem('RecallFeedId')
            localStorage.removeItem('savedStep')

            if (sourceId) {
                // localStorage.setItem('RecallFeedSourceId', sourceId)
                this.$router.push({
                    name: 'prov_Recall_NewItem',
                    params: { recallFeedSourceId: sourceId }
                })
                return
            }

            this.$router.push({ name: 'prov_Recall_NewItem',  params: {
                aiRecallJobId: this.uploadedFilesJobId
            } })
        },
        routeToManualRecallEntry() {
            this.routeToRecallEntry()
        },
        onCancelProcessing() {
            this._showConfirm(
                CANCEL_PROCESSING_CONFIRM_MESSAGE,
                {
                    title: 'Cancel Processing',
                    variant: 'warning',
                    saveLabel: 'Yes',
                    cancelLabel: 'No'
                }
            ).then(async confirmed => {
                if (confirmed) {
                    this.cleanupRecallAiTimers()
                    if (this.processingJobId) {
                        try {
                            await recallService.cancelRecallAIJob(this.processingJobId)
                        } catch (error) {
                            // Continue with hide even if cancel API fails
                        }
                    }
                    this.processingJobId = null
                    this.$refs.modal.hide()
                }
            })
        },
        onTimeoutAlertOK() {
            this.showTimeoutAlert = false
            this.processingProgressPaused = false
            this.startRecallStatusPolling()
            // Extend the max window by 30 seconds
            this.processingMaxWindowMs += 30000
        },
        async onTimeoutAlertCancel() {
            this.showTimeoutAlert = false
            this.cleanupRecallAiTimers()
            this.isAiProcessing = false
            this.processingProgressPaused = true
            // Fire cancel API request with Job ID
            if (this.processingJobId) {
                try {
                    await recallService.cancelRecallAIJob(this.processingJobId)
                } catch (error) {
                    // Continue with routing even if cancel API fails
                }
            }
            this.processingJobId = null
            this.$refs.modal.hide()
            // this.routeToManualRecallEntry()
        },
        skipStep() {
            this.$refs.modal.hide()
            this.$router.push({ name: 'prov_Recall_NewItem' })
        },
    },
}
